import React, { useState, useMemo } from 'react';
import {
  ArrowLeftIcon,
  ArrowUturnLeftIcon,
  PaperClipIcon,
  ArrowDownTrayIcon,
  UserCircleIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';

const EmailDetail = ({ email, loading, onBack, onReply, onDownloadAttachment }) => {
  const [allowExternalContent, setAllowExternalContent] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString([], {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 0) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Sanitize HTML content for safe rendering
  const sanitizedHtml = useMemo(() => {
    if (!email) return '';

    // If allowing external content, use raw HTML but still sanitize dangerous elements
    const htmlToSanitize = allowExternalContent ? email.rawBodyHtml : email.bodyHtml;

    if (!htmlToSanitize) return '';

    // Configure DOMPurify
    const config = {
      ALLOWED_TAGS: [
        'a', 'abbr', 'address', 'b', 'blockquote', 'br', 'caption', 'cite', 'code',
        'col', 'colgroup', 'dd', 'del', 'dfn', 'div', 'dl', 'dt', 'em', 'figcaption',
        'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'ins', 'kbd',
        'li', 'mark', 'ol', 'p', 'pre', 'q', 's', 'samp', 'small', 'span', 'strong',
        'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
        'var', 'wbr', 'font', 'center', 'style'
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class', 'id', 'style', 'target', 'rel',
        'width', 'height', 'border', 'cellpadding', 'cellspacing', 'align', 'valign',
        'bgcolor', 'color', 'face', 'size', 'data-blocked-src'
      ],
      ALLOW_DATA_ATTR: true,
      ADD_ATTR: ['target'],
      // Force all links to open in new tab
      FORCE_BODY: true,
    };

    // Hook to add target="_blank" and rel="noopener noreferrer" to all links
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });

    const sanitized = DOMPurify.sanitize(htmlToSanitize, config);

    // Clean up hook
    DOMPurify.removeHook('afterSanitizeAttributes');

    return sanitized;
  }, [email, allowExternalContent]);

  // Reset allowExternalContent when email changes
  React.useEffect(() => {
    setAllowExternalContent(false);
  }, [email?.uid]);

  const hasBlockedContent = email?.contentBlocking?.hasBlockedContent;
  const blockedImagesCount = email?.contentBlocking?.blockedImagesCount || 0;
  const blockedScriptsCount = email?.contentBlocking?.blockedScriptsCount || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <UserCircleIcon className="h-16 w-16 mb-4" />
        <p className="text-lg">Select an email to read</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100 lg:hidden"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 truncate">{email.subject}</h2>
        </div>
        <button
          onClick={() => onReply(email)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ArrowUturnLeftIcon className="h-4 w-4" />
          Reply
        </button>
      </div>

      {/* Blocked Content Banner */}
      {hasBlockedContent && !allowExternalContent && (
        <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center gap-3">
            <ShieldExclamationIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-medium text-amber-800">External content blocked</span>
              <span className="text-amber-700 ml-1">
                ({blockedImagesCount > 0 && `${blockedImagesCount} image${blockedImagesCount > 1 ? 's' : ''}`}
                {blockedImagesCount > 0 && blockedScriptsCount > 0 && ', '}
                {blockedScriptsCount > 0 && `${blockedScriptsCount} script${blockedScriptsCount > 1 ? 's' : ''}`})
              </span>
              <p className="text-amber-600 text-xs mt-0.5">
                Remote images and scripts are blocked to protect your privacy.
              </p>
            </div>
          </div>
          <button
            onClick={() => setAllowExternalContent(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors flex-shrink-0"
          >
            <ShieldCheckIcon className="h-4 w-4" />
            Allow External Content
          </button>
        </div>
      )}

      {/* External Content Allowed Banner */}
      {hasBlockedContent && allowExternalContent && (
        <div className="flex items-center justify-between px-4 py-2 bg-green-50 border-b border-green-200">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">External content is now displayed</span>
          </div>
          <button
            onClick={() => setAllowExternalContent(false)}
            className="text-sm text-green-700 hover:text-green-800 underline"
          >
            Block again
          </button>
        </div>
      )}

      {/* Email Info */}
      <div className="px-4 py-4 border-b border-gray-200">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-lg">
                {(email.from || email.fromEmail || '?')[0].toUpperCase()}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{email.from || email.fromEmail}</p>
                <p className="text-sm text-gray-500">{email.fromEmail}</p>
              </div>
              <span className="text-sm text-gray-500">{formatDate(email.date)}</span>
            </div>
            <div className="mt-1 text-sm text-gray-600">
              <span>To: {email.to?.join(', ')}</span>
              {email.cc && email.cc.length > 0 && (
                <span className="ml-4">CC: {email.cc.join(', ')}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Attachments */}
      {email.attachments && email.attachments.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <PaperClipIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              {email.attachments.length} Attachment{email.attachments.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {email.attachments.map((attachment) => (
              <button
                key={attachment.index}
                onClick={() => onDownloadAttachment(attachment)}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <ArrowDownTrayIcon className="h-4 w-4 text-gray-500" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                    {attachment.filename}
                  </p>
                  <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Email Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {email.bodyHtml || email.rawBodyHtml ? (
          <div
            className="prose prose-sm max-w-none email-content"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
            {email.bodyText}
          </pre>
        )}
      </div>
    </div>
  );
};

export default EmailDetail;

